import React, { memo } from "react";

import { useFormContext } from "react-hook-form";

import {
  Accordion,
  AccordionGroup,
  Box,
  Card,
  Column,
  Columns,
  Heading,
  List,
  ListItem,
  Stack,
  Text,
} from "@origin-digital/ods-core";
import { IconHome, IconSunny } from "@origin-digital/ods-icons";

import {
  IconDollar,
  IconLightning,
  IconMoon,
  IconSolarEnergyGenerated,
} from "@/components/icons";
import { USAGE_PATTERNS } from "@/constants";
import {
  BillFrequencyEnum,
  IISystemSizeMinMaxResponse,
} from "@/contract/generated/electrify-v1";
import { formatLabel, formatNumberWithCommas } from "@/utils";

import { solarOutlook as content } from "../content.json";

type GenerationProps = {
  fitPrice: number;
  recommendationSystem: IISystemSizeMinMaxResponse;
};

export const Generation = memo(
  ({ fitPrice, recommendationSystem }: GenerationProps) => {
    const { label, testId } = content;
    const { getValues } = useFormContext();
    const {
      billSavingPeriod,
      billAmount,
      averageDailyGenerationAccurassi,
      percentageOffset,
      excessGridEnergyDollarsSavingsForBillPeriod,
      dayTimeUsageInKwh = 0,
      dayTimePercentage,
      eveningUsageInKwh = 0,
      eveningPercentage,
    } = recommendationSystem;

    const billPeriod = billSavingPeriod
      ? billSavingPeriod.toLowerCase()
      : BillFrequencyEnum["MONTHLY"].toLowerCase();

    return (
      <Box
        data-id={testId.generation}
        display="flex"
        marginX={{ xs: "none", lg: "medium" }}
        marginY={{ xs: "small", lg: "none" }}
        width="full"
      >
        <Card outline>
          <Stack space={["medium", "large"]}>
            <Heading variant="h4" weight="bold">
              {label.solarSystemGenerateForMe.heading}
            </Heading>
            <Stack space="large">
              <List>
                <ListItem icon={<IconSolarEnergyGenerated color="secondary" />}>
                  <Stack space="none">
                    <Heading variant="h4" weight="bold">
                      {`${averageDailyGenerationAccurassi} kWh`}
                    </Heading>
                    <Text variant="body-small">
                      {label.solarSystemGenerateForMe.generatedPower}
                    </Text>
                  </Stack>
                </ListItem>
                <ListItem icon={<IconLightning color="secondary" />}>
                  <Stack space="none">
                    <Heading variant="h4" weight="bold">
                      {`${percentageOffset}%`}
                    </Heading>
                    <Text variant="body-small">
                      {label.solarSystemGenerateForMe.percentageOfOffset}
                    </Text>
                  </Stack>
                </ListItem>
                <ListItem icon={<IconDollar color="secondary" />}>
                  <Stack space="none">
                    <Heading variant="h4" weight="bold">
                      {`$${formatNumberWithCommas(
                        excessGridEnergyDollarsSavingsForBillPeriod
                      )}`}
                    </Heading>
                    <Text variant="body-small">
                      {formatLabel(
                        label.solarSystemGenerateForMe.excessSolarPerFrequency,
                        (value) => (
                          <b>{value}</b>
                        ),
                        billPeriod,
                        `${fitPrice}c/kWh`
                      )}
                    </Text>
                  </Stack>
                </ListItem>
              </List>

              <AccordionGroup>
                <Accordion id="" size="small" summary="About your energy use">
                  <Stack space="medium">
                    <Stack space="xxsmall">
                      <Columns space="xsmall">
                        <Column width="content">
                          <IconHome color="neutralDark" />
                        </Column>
                        <Column>
                          <Text weight="medium" tone="neutralDark">
                            {label.aboutEnergyUse.dailyEnergyUse}
                          </Text>
                        </Column>
                        <Column width="1/3">
                          <Text
                            weight="medium"
                            tone="neutralDark"
                            align="right"
                          >
                            {`${formatNumberWithCommas(
                              dayTimeUsageInKwh + eveningUsageInKwh
                            )} kWh`}
                          </Text>
                        </Column>
                        <Column width="1/6" />
                      </Columns>

                      <Columns space="xsmall">
                        <Column width="content">
                          <IconSunny />
                        </Column>
                        <Column>
                          <Text tone="neutralDark">
                            {label.aboutEnergyUse.daytimeUse}
                          </Text>
                        </Column>
                        <Column width="1/3">
                          <Text tone="neutralDark" align="right">
                            {`${formatNumberWithCommas(dayTimeUsageInKwh)} kWh`}
                          </Text>
                        </Column>
                        <Column width="1/6">
                          <Text tone="neutralDark" align="right">
                            {`${formatNumberWithCommas(dayTimePercentage, 0)}%`}
                          </Text>
                        </Column>
                      </Columns>

                      <Columns space="xsmall">
                        <Column width="content">
                          <IconMoon color="promote" />
                        </Column>
                        <Column>
                          <Text tone="neutralDark">
                            {label.aboutEnergyUse.eveningUse}
                          </Text>
                        </Column>
                        <Column width="1/3">
                          <Text tone="neutralDark" align="right">
                            {`${formatNumberWithCommas(eveningUsageInKwh)} kWh`}
                          </Text>
                        </Column>
                        <Column width="1/6">
                          <Text tone="neutralDark" align="right">
                            {`${formatNumberWithCommas(eveningPercentage, 0)}%`}
                          </Text>
                        </Column>
                      </Columns>
                    </Stack>

                    <Text variant="body-small">
                      {formatLabel(
                        label.aboutEnergyUse.description,
                        (value) => (
                          <b>{value}</b>
                        ),
                        billPeriod,
                        `$${formatNumberWithCommas(billAmount)}`,
                        `'${USAGE_PATTERNS[getValues("usagePattern")]}'`
                      )}
                    </Text>
                  </Stack>
                </Accordion>
              </AccordionGroup>
            </Stack>
          </Stack>
        </Card>
      </Box>
    );
  }
);
